// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@intakeform/ui';
import { useQuestionnaires } from '../../api/questionnaires.js';
import type { QuestionnaireSummary } from '../../api/types.js';
import {
  useCreateAutofillJob,
  useEmrPatients,
  type EmrPatientSummary,
} from '../../api/autofill.js';

/**
 * Sort forms newest-first by `updatedAt` so the picker surfaces the
 * version a clinician is most likely to want. Stable across re-renders
 * because we sort a copy.
 */
function sortedFormsForPicker(items: QuestionnaireSummary[]): QuestionnaireSummary[] {
  return [...items].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/**
 * Build the dropdown label. When several forms share a title (e.g.
 * test-cruft or two real edits of the same form name) we append a
 * disambiguating suffix — last-updated date + short id — so the
 * clinician can tell which one they're picking. Clean single-version
 * forms get a plain `Title (v1)` label.
 *
 * The dedupe lookup happens per-render against the latest items list,
 * not once on mount, so renames/inserts reflect immediately.
 */
function labelForForm(q: QuestionnaireSummary): string {
  const shortId = q.id.slice(0, 6);
  const date = q.updatedAt.slice(0, 10);
  return `${q.title} (v${String(q.version)} · ${date} · ${shortId})`;
}

/**
 * Step 1 of the autofill flow: an authorised user (admin | clinician |
 * doctor | reception) picks a published Questionnaire + a patient from
 * the connected EMR and kicks off the job. On success we navigate
 * straight to the admin preview route.
 */
export function AutofillCreateRoute() {
  const navigate = useNavigate();
  const forms = useQuestionnaires();
  const [search, setSearch] = useState('');
  const [mrn, setMrn] = useState('');
  const patients = useEmrPatients(search, mrn);
  const exactMrn = mrn.trim();
  const create = useCreateAutofillJob();

  const [selectedQ, setSelectedQ] = useState<string>('');
  const [selectedPatient, setSelectedPatient] = useState<EmrPatientSummary | null>(null);

  const ready = selectedQ !== '' && selectedPatient !== null && !create.isPending;

  const submit = async (): Promise<void> => {
    if (!selectedPatient || selectedQ === '') return;
    try {
      const job = await create.mutateAsync({
        questionnaireId: selectedQ,
        patientId: selectedPatient.id,
      });
      navigate(`/autofill/${job.id}/preview`);
    } catch {
      // surfaced via create.error
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Auto-fill from EMR</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pull a patient's demographics from the connected EMR into a form draft for review.
        </p>
      </header>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">1. Pick a form</h2>
        {forms.isLoading ? (
          <p className="muted">Loading forms…</p>
        ) : forms.error ? (
          <div className="error-banner">Failed to load forms.</div>
        ) : (
          <select
            value={selectedQ}
            onChange={(e) => setSelectedQ(e.currentTarget.value)}
            className="w-full max-w-md rounded-md border border-border bg-surface p-2"
            data-testid="autofill-form-select"
          >
            <option value="">— Select a form —</option>
            {sortedFormsForPicker(forms.data ?? []).map((q) => (
              <option key={q.id} value={q.id}>
                {labelForForm(q)}
              </option>
            ))}
          </select>
        )}
      </section>

      <section className="space-y-3">
        <h2 className="text-base font-semibold">2. Pick a patient</h2>
        <input
          type="text"
          placeholder="Search by name, EMR id, or email…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          disabled={exactMrn !== ''}
          className="w-full max-w-md rounded-md border border-border bg-surface p-2 disabled:opacity-50"
          data-testid="autofill-patient-search"
        />
        {/*
          A separate field rather than parsing the box above. Searching by name
          is a guess the EMR is free to interpret; an MRN asserts one exact
          record and is matched exactly or not at all. Keeping them apart means
          the clinician always knows which search produced what they are
          looking at (intakeform-preview-200.7.7).
        */}
        <div className="max-w-md space-y-1">
          <label htmlFor="autofill-patient-mrn" className="text-sm font-medium">
            Or look up an exact MRN
          </label>
          <input
            id="autofill-patient-mrn"
            type="text"
            placeholder="Medical record number"
            value={mrn}
            onChange={(e) => setMrn(e.currentTarget.value)}
            maxLength={64}
            className="w-full rounded-md border border-border bg-surface p-2"
            data-testid="autofill-patient-mrn"
          />
          {exactMrn !== '' ? (
            <p className="text-xs text-muted-foreground">
              Exact match only — name search is paused while this is filled in.
            </p>
          ) : null}
        </div>
        {patients.isLoading ? (
          <p className="muted">Searching…</p>
        ) : patients.error ? (
          <div className="error-banner">EMR lookup failed.</div>
        ) : (patients.data ?? []).length === 0 ? (
          exactMrn !== '' ? (
            /*
              Say it plainly rather than quietly showing an empty picker. An
              MRN that matches nothing is a real signal — the number is wrong,
              or this patient is not in the connected EMR — and it is the same
              signal 200.7.8 has to fail closed on.
            */
            <div className="error-banner" role="status">
              No patient in the connected EMR has MRN {exactMrn}. Check the number, or clear it to
              search by name.
            </div>
          ) : (
            <p className="muted">No patients found.</p>
          )
        ) : (
          <ul className="divide-y divide-border border border-border rounded-md max-w-2xl">
            {(patients.data ?? []).map((p) => {
              const isSelected = selectedPatient?.id === p.id;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedPatient(p)}
                    data-testid={`autofill-patient-row-${p.id}`}
                    className={`w-full text-left p-3 hover:bg-neutral-50/50 dark:hover:bg-neutral-800/10 ${
                      isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                    }`}
                  >
                    <div className="font-medium">{p.displayName}</div>
                    <div className="text-xs text-muted-foreground">
                      EMR id {p.id}
                      {p.birthDate ? ` · DOB ${p.birthDate}` : ''}
                      {p.email ? ` · ${p.email}` : ''}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="flex items-center gap-3">
        <Button
          type="button"
          onClick={() => {
            void submit();
          }}
          disabled={!ready}
          data-testid="autofill-create-submit"
        >
          {create.isPending ? 'Creating…' : 'Create autofill job'}
        </Button>
        {create.error ? (
          <span className="error-banner" role="alert">
            {create.error.message}
          </span>
        ) : null}
      </section>
    </div>
  );
}
